import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import toast from 'react-hot-toast'
import Modal from '../../Unknown/Modal'
import ConfirmActionOtpModal from '../ConfirmActionOtpModal'
import CustomPhoneInput from '../../UI/PhoneInput'
import translate from '../../../i18n/translate'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import {
  notificationApi,
  PushNotificationAudience,
} from '../../../modules/services/notificationService'

type TabKind = 'custom' | 'docsUpdated'

const CUSTOM_AUDIENCES: PushNotificationAudience[] = [
  'allOrganizers',
  'activeOrganizers',
  'notActiveOrganizers',
  'allUsers',
]

const DOCS_AUDIENCES: PushNotificationAudience[] = [
  'allOrganizers',
  'activeOrganizers',
  'notActiveOrganizers',
  'allUsers',
]

function extractErrorMessage(res: any): string | null {
  const data = res?.data
  if (data?.message?.message) return String(data.message.message)
  if (typeof data?.message === 'string') return data.message
  const errData = res?.error?.data
  if (errData?.message?.message) return String(errData.message.message)
  if (typeof errData?.message === 'string') return errData.message
  return null
}

export default function SendPushNotificationModal() {
  const dispatch = useAppDispatch()
  const { toggleSendPushNotificationModal } = modalsSlice.actions
  const visible = useAppSelector((state) => state.modalsReducer.sendPushNotification)

  const [tab, setTab] = useState<TabKind>('custom')
  const [audience, setAudience] = useState<PushNotificationAudience>('allOrganizers')
  const [titleEn, setTitleEn] = useState('')
  const [titleHe, setTitleHe] = useState('')
  const [textEn, setTextEn] = useState('')
  const [textHe, setTextHe] = useState('')
  const [isTestMode, setIsTestMode] = useState(false)
  const [testPhone, setTestPhone] = useState('+972')
  const [testUserName, setTestUserName] = useState('')
  const [otpOpen, setOtpOpen] = useState(false)

  const usesUserName = useMemo(
    () => tab === 'custom' && (textEn.includes('%USER_NAME%') || textHe.includes('%USER_NAME%')),
    [tab, textEn, textHe]
  )

  const { data: docsText, isFetching: isFetchingDocs } =
    notificationApi.useGetDocsUpdateNotificationTextQuery(undefined, {
      skip: visible !== 'open' || tab !== 'docsUpdated',
      refetchOnMountOrArgChange: true,
    })

  const [sendOtp, { isLoading: isSendingOtp }] =
    notificationApi.useSendPushNotificationOtpMutation()
  const [sendTest, { isLoading: isSendingTest }] =
    notificationApi.useSendTestPushNotificationMutation()
  const [confirmSend, { isLoading: isConfirming }] =
    notificationApi.useConfirmSendPushNotificationMutation()

  const audienceOptions = tab === 'custom' ? CUSTOM_AUDIENCES : DOCS_AUDIENCES

  useEffect(() => {
    if (!audienceOptions.includes(audience)) {
      setAudience(audienceOptions[0])
    }
  }, [tab, audience, audienceOptions])

  const canSendCustom = useMemo(() => {
    if (tab !== 'custom') return true
    const hasTitle = titleEn.trim() || titleHe.trim()
    const hasText = textEn.trim() || textHe.trim()
    return Boolean(hasTitle && hasText)
  }, [tab, titleEn, titleHe, textEn, textHe])

  const resetForm = () => {
    setTab('custom')
    setAudience('allOrganizers')
    setTitleEn('')
    setTitleHe('')
    setTextEn('')
    setTextHe('')
    setIsTestMode(false)
    setTestPhone('+972')
    setTestUserName('')
    setOtpOpen(false)
  }

  const onCloseModal = () => {
    resetForm()
    dispatch(toggleSendPushNotificationModal())
  }

  const buildCustomPayload = () =>
    tab === 'custom'
      ? {
          titleEn: titleEn.trim() || undefined,
          titleHe: titleHe.trim() || undefined,
          textEn: textEn.trim() || undefined,
          textHe: textHe.trim() || undefined,
        }
      : {}

  const handleSend = async () => {
    if (tab === 'custom' && !canSendCustom) {
      toast.error(translate('sendPushNotificationValidationTitleTextRequired'))
      return
    }

    let normalizedTestPhone: string | undefined
    if (isTestMode) {
      if (!testPhone || testPhone.length < 8) {
        toast.error(translate('sendPushNotificationTestPhoneRequired'))
        return
      }
      normalizedTestPhone = testPhone.startsWith('+') ? testPhone : `+${testPhone}`
      if (usesUserName && !testUserName.trim()) {
        toast.error(translate('sendPushNotificationTestUserNameRequired'))
        return
      }
    }

    // A test goes to a single user, so it is sent without OTP and the modal stays open with the text intact —
    // the admin checks the push on the phone and then sends the real broadcast from the same form.
    if (isTestMode) {
      const res: any = await sendTest({
        kind: tab,
        testPhone: normalizedTestPhone,
        testUserName: testUserName.trim() || undefined,
        ...buildCustomPayload(),
      })
      const errorMessage = extractErrorMessage(res)
      if (errorMessage) {
        toast.error(errorMessage)
        return
      }
      toast.success(translate('sendPushNotificationTestSuccess'))
      return
    }

    const res: any = await sendOtp({
      kind: tab,
      audience,
      ...buildCustomPayload(),
    })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }
    setOtpOpen(true)
    toast.success(translate('sendPushNotificationOtpSent'))
  }

  const handleResendOtp = async () => {
    const res: any = await sendOtp({
      kind: tab,
      audience,
      ...buildCustomPayload(),
    })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }
    toast.success(translate('sendPushNotificationOtpSent'))
  }

  const handleConfirmOtp = async (finalCode: string) => {
    const res: any = await confirmSend({ code: finalCode })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }
    toast.success(translate('sendPushNotificationSendStarted'))
    resetForm()
    dispatch(toggleSendPushNotificationModal())
  }

  const isBusy = isSendingOtp || isSendingTest || isConfirming

  const renderLangBlock = (lang: 'en' | 'he') => {
    const isEn = lang === 'en'
    const titleValue = isEn ? titleEn : titleHe
    const textValue = isEn ? textEn : textHe
    const onTitle = isEn ? setTitleEn : setTitleHe
    const onText = isEn ? setTextEn : setTextHe

    return (
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        gap={1}
        p={1.5}
        sx={{
          border: '1px solid #f0e6d8',
          borderRadius: 1,
          backgroundColor: '#fffaf3',
          direction: isEn ? 'ltr' : 'rtl',
        }}
      >
        <Typography variant="caption" color="textSecondary" sx={{ direction: 'ltr' }}>
          {isEn ? 'EN' : 'HE'}
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          color="warning"
          label={isEn ? translate('sendPushNotificationTitleEn') : translate('sendPushNotificationTitleHe')}
          value={titleValue}
          onChange={(e) => onTitle(e.target.value)}
          disabled={isBusy}
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          size="small"
          variant="outlined"
          color="warning"
          label={isEn ? translate('sendPushNotificationTextEn') : translate('sendPushNotificationTextHe')}
          value={textValue}
          onChange={(e) => onText(e.target.value)}
          disabled={isBusy}
          multiline
          minRows={3}
          maxRows={8}
          inputProps={{ maxLength: 1000 }}
        />
      </Box>
    )
  }

  const renderDocsBlock = (lang: 'en' | 'he') => {
    const isEn = lang === 'en'
    const content = isEn ? docsText?.en : docsText?.he
    return (
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        gap={0.5}
        p={1.5}
        sx={{
          border: '1px solid #f0e6d8',
          borderRadius: 1,
          backgroundColor: '#fffaf3',
          direction: isEn ? 'ltr' : 'rtl',
        }}
      >
        <Typography variant="caption" color="textSecondary" sx={{ direction: 'ltr' }}>
          {isEn ? 'EN' : 'HE'}
        </Typography>
        <Typography variant="subtitle1" fontWeight={700}>
          {content?.title}
        </Typography>
        <Typography variant="body2" whiteSpace="pre-line">
          {content?.description}
        </Typography>
      </Box>
    )
  }

  return (
    <Modal
      title={translate('sendPushNotificationTitle')}
      visible={visible === 'open'}
      style={{ maxWidth: '720px' }}
      setVisible={onCloseModal}
    >
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          textColor="secondary"
          TabIndicatorProps={{ style: { backgroundColor: '#ED6C02' } }}
          sx={{
            '.MuiTab-root': { color: '#8b8b8b' },
            '.MuiTab-root.Mui-selected': { color: '#ED6C02' },
          }}
        >
          <Tab value="custom" label={translate('sendPushNotificationTabCustom')} />
          <Tab value="docsUpdated" label={translate('sendPushNotificationTabDocs')} />
        </Tabs>

        {tab === 'custom' && (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="caption" color="textSecondary">
              {translate('sendPushNotificationCustomHelper')}
            </Typography>
            <Box display="flex" gap={1.5} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
              {renderLangBlock('en')}
              {renderLangBlock('he')}
            </Box>
          </Box>
        )}

        {tab === 'docsUpdated' && (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="caption" color="textSecondary">
              {translate('sendPushNotificationDocsHelper')}
            </Typography>
            {isFetchingDocs ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress color="warning" size={24} />
              </Box>
            ) : (
              <Box display="flex" gap={1.5} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                {renderDocsBlock('en')}
                {renderDocsBlock('he')}
              </Box>
            )}
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="subtitle2">{translate('sendPushNotificationAudience')}</Typography>
          <RadioGroup value={audience} onChange={(e) => setAudience(e.target.value as PushNotificationAudience)}>
            {audienceOptions.map((opt) => (
              <FormControlLabel
                key={opt}
                value={opt}
                disabled={isBusy || isTestMode}
                control={<Radio color="warning" size="small" />}
                label={translate(`sendPushNotificationAudience_${opt}`)}
              />
            ))}
          </RadioGroup>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              color="warning"
              checked={isTestMode}
              onChange={(e) => setIsTestMode(e.target.checked)}
              disabled={isBusy}
            />
          }
          label={translate('sendPushNotificationTestCheckbox')}
        />

        {isTestMode && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                {translate('sendPushNotificationTestPhoneLabel')}
              </Typography>
              <CustomPhoneInput phone={testPhone} setPhone={setTestPhone} />
            </Box>
            {usesUserName && (
              <TextField
                size="small"
                variant="outlined"
                color="warning"
                label={translate('sendPushNotificationTestUserNameLabel')}
                helperText={translate('sendPushNotificationTestUserNameHelper')}
                value={testUserName}
                onChange={(e) => setTestUserName(e.target.value)}
                disabled={isBusy}
                inputProps={{ maxLength: 200 }}
              />
            )}
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            color="warning"
            variant="contained"
            disabled={isBusy}
            onClick={handleSend}
          >
            {isSendingOtp || isSendingTest ? (
              <CircularProgress size={20} />
            ) : isTestMode ? (
              translate('sendPushNotificationSendTest')
            ) : (
              translate('sendPushNotificationSend')
            )}
          </Button>
        </Box>
      </Box>

      <ConfirmActionOtpModal
        visible={otpOpen}
        title={translate('sendPushNotificationOtpModalTitle')}
        description={translate('sendPushNotificationOtpModalDescription')}
        onClose={() => setOtpOpen(false)}
        onConfirm={handleConfirmOtp}
        onResend={handleResendOtp}
        isConfirming={isConfirming}
        isResending={isSendingOtp}
      />
    </Modal>
  )
}
